import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ApiError, messageOf } from "../api/http";
import { readRunRecord, type RunRecord, type RunTicket, type SlackPost } from "../api/runRecord";
import {
  findingEvidence,
  isTerminal,
  type FindingEvidence,
  refusalNotice,
  type AlternativeClaim,
  type Failure,
  type Retraction,
  type RunProjection,
  type RunStatus,
  type TimelineRow,
} from "../features/ops/investigationProjection";
import { useRunProjection } from "../useRunProjection";
import { ChangeAudit } from "./ChangeAudit";
import { ChatPanel } from "./ChatPanel";
import { Chip, Notice } from "./chrome";
import { queueName, SlackPostLink, TicketLink } from "./InvestigationLinks";
import type { RefusedConclusion } from "./confidence";
import { CONFIDENCE_HELP, confidenceBand, confidenceReasons, refusedConclusion } from "./confidence";
import { DESTINATIONS } from "./routes";
import { INBOX_KINDS, filterPath } from "./inboxView";
import { readableTime as time, runStatusLabel, runStatusTone } from "./runPresentation";

const SECTIONS = ["Summary", "Evidence", "Timeline", "Chat"] as const;
type Section = (typeof SECTIONS)[number];

export function RunPage({ runId, decisions }: { runId: string; decisions?: (projection: RunProjection) => ReactNode }) {
  const { projection, reconnecting } = useRunProjection(runId);
  const [record, setRecord] = useState<RunRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A read again that failed while the page shows this run's record (#1374): the record
  // stays, and a quiet note says its latest figures could not be read.
  const [stale, setStale] = useState(false);
  const shown = useRef<RunRecord | null>(null);
  shown.current = record;
  const [reload, setReload] = useState(0);
  const [section, setSection] = useState<Section>("Summary");
  /** A link in the Summary to a record in Evidence: switch tab, then open and focus it (as Supporting findings does). */
  const inspectTool = (anchor: string) => {
    setSection("Evidence");
    requestAnimationFrame(() => {
      const detail = document.getElementById(anchor) as HTMLDetailsElement | null;
      if (detail) {
        detail.open = true;
        detail.querySelector("summary")?.focus();
      }
    });
  };
  useEffect(() => {
    let live = true;
    setError(null);
    void readRunRecord(runId).then(
      (value) => {
        if (!live) return;
        setRecord(value);
        setStale(false);
      },
      (reason: unknown) => {
        if (!live) return;
        if (shown.current?.run_id === runId) setStale(true);
        else
          setError(
            reason instanceof ApiError && reason.status === 404
              ? "This investigation is unavailable in the selected workspace. Return to investigations or select the workspace that holds it."
              : reason instanceof ApiError && reason.status === 401
                ? "Your session has expired. Reload the page to sign in again."
                : `${messageOf(reason)} Try reading the investigation again.`,
          );
      },
    );
    return () => {
      live = false;
    };
  }, [runId, reload]);
  // The header's Started and Finished come off the frames (#1374). What no frame carries
  // is the ledger's debit, written in the transaction of the terminal it prices (#1373):
  // so the record is read again once when the stream shows the run's terminal and the
  // record was read before it, and once for each chat turn's terminal the record's
  // `last_seq` does not yet cover.
  const ended = isTerminal(projection.status);
  const chatTurnEnded = latestChatTurnTerminal(projection.timeline);
  const reread = useRef({ runId, ended: false, chatTurn: 0 });
  useEffect(() => {
    if (!record) return;
    if (reread.current.runId !== runId) reread.current = { runId, ended: false, chatTurn: 0 };
    const asked = reread.current;
    const finished = ended && !record.finished_at && !asked.ended;
    const turned = chatTurnEnded > record.last_seq && chatTurnEnded > asked.chatTurn;
    if (!finished && !turned) return;
    reread.current = {
      runId,
      ended: asked.ended || finished,
      chatTurn: turned ? chatTurnEnded : asked.chatTurn,
    };
    setReload((value) => value + 1);
  }, [runId, record, ended, chatTurnEnded]);
  const status = projection.status ?? record?.status ?? "waiting";
  const title =
    record?.title ?? record?.fingerprint ?? `Investigation ${runId}`;
  function changeSection(
    event: KeyboardEvent<HTMLButtonElement>,
    current: Section,
  ) {
    const index = SECTIONS.indexOf(current);
    const next =
      event.key === "ArrowRight"
        ? (index + 1) % SECTIONS.length
        : event.key === "ArrowLeft"
          ? (index + SECTIONS.length - 1) % SECTIONS.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? SECTIONS.length - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    const selected = SECTIONS[next]!;
    setSection(selected);
    document.getElementById(`investigation-tab-${selected}`)?.focus();
  }
  return (
    <section className="investigation-page">
      <a
        href={DESTINATIONS.find((item) => item.id === "runs")!.path}
        className="run-page__back"
      >
        ← Investigations
      </a>
      <article className="investigation-sheet">
        <header className="investigation-heading">
          <div className="investigation-status">
            <span
              data-testid="status"
              className={`tag tag--${runStatusTone(status)}`}
            >
              {runStatusLabel(status)}
            </span>
            <span role="status" className="muted">
              {reconnecting
                ? "Reconnecting… Showing the last received events."
                : projection.seq > 0
                  ? "Receiving investigation updates"
                  : "Connecting to investigation updates…"}
            </span>
          </div>
          <h1>{title}</h1>
          <p className="muted investigation-id">
            <code>{runId}</code>
          </p>
          {record ? (
            <>
              <RunMeta record={record} clocks={projection} />
              <RunCostLines record={record} />
              {stale ? (
                <p role="status" className="muted">
                  The latest figures for this investigation couldn&apos;t be read.
                </p>
              ) : null}
            </>
          ) : error ? null : (
            <p role="status" className="muted">
              Reading investigation details…
            </p>
          )}
        </header>
        {error ? (
          <div className="stack stack--tight">
            <Notice tone="bad">{error}</Notice>
            <button
              className="btn btn--quiet"
              onClick={() => setReload((value) => value + 1)}
            >
              Read investigation again
            </button>
          </div>
        ) : null}
        <div
          className="investigation-tabs"
          role="tablist"
          aria-label="Investigation sections"
        >
          {SECTIONS.map((name) => (
            <button
              key={name}
              id={`investigation-tab-${name}`}
              role="tab"
              aria-selected={section === name}
              aria-controls={`investigation-panel-${name}`}
              tabIndex={section === name ? 0 : -1}
              onKeyDown={(event) => changeSection(event, name)}
              onClick={() => setSection(name)}
            >
              {name}
              {name === "Evidence" ? (
                <span className="muted">
                  {" "}
                  {projection.findings.length + projection.hypotheses.length}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="investigation-layout">
          <div className="investigation-reading">
            <section
              id="investigation-panel-Summary"
              role="tabpanel"
              aria-labelledby="investigation-tab-Summary"
              hidden={section !== "Summary"}
              tabIndex={0}
            >
              <Summary projection={projection} status={status} onInspect={inspectTool} />
              {projection.findings.length > 0 ? (
                <section className="investigation-support">
                  <h2>Supporting findings</h2>
                  <ul>
                    {projection.findings.map((finding, index) => (
                      <li
                        key={finding.finding_id || index}
                        id={supportingFindingId(index)}
                      >
                        <span className="investigation-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <button
                          className="investigation-finding-link"
                          onClick={() => {
                            setSection("Evidence");
                            requestAnimationFrame(() => {
                              const detail = document.getElementById(
                                `finding-${index}`,
                              ) as HTMLDetailsElement | null;
                              if (detail) {
                                detail.open = true;
                                detail.querySelector("summary")?.focus();
                              }
                            });
                          }}
                        >
                          {finding.summary || "Untitled finding"}
                        </button>
                        {finding.retraction ? (
                          <RetractedMark retraction={finding.retraction} />
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : (
                <p className="muted investigation-support">
                  No findings yet. Check Timeline for investigation progress.
                </p>
              )}
            </section>
            <section
              id="investigation-panel-Evidence"
              role="tabpanel"
              aria-labelledby="investigation-tab-Evidence"
              hidden={section !== "Evidence"}
              tabIndex={0}
            >
              <Evidence projection={projection} />
            </section>
            <section
              id="investigation-panel-Timeline"
              role="tabpanel"
              aria-labelledby="investigation-tab-Timeline"
              hidden={section !== "Timeline"}
              tabIndex={0}
            >
              <Timeline projection={projection} />
            </section>
            <section
              id="investigation-panel-Chat"
              role="tabpanel"
              aria-labelledby="investigation-tab-Chat"
              hidden={section !== "Chat"}
              tabIndex={0}
            >
              {section === "Chat" ? (
                <ChatPanel key={runId} runId={runId} projection={projection} />
              ) : null}
            </section>
          </div>
          <ProposalArea projection={projection}>{decisions?.(projection)}</ProposalArea>
        </div>
      </article>
    </section>
  );
}

/** One absence claim as the operator reads it: its disposition, its bound, and what the runtime did to it (#523). */
function claimLine(claim: AlternativeClaim): string {
  if (!claim.disposition) return claim.statement;
  const parts = [claim.disposition === "ruled_out" ? "ruled out" : claim.disposition.replace(/_/g, " ")];
  if (claim.claimed_through) parts[0] += ` through ${claim.claimed_through}`;
  const why: Record<string, string> = {
    past_horizon: "narrowed to what its read observed",
    not_windowed: "unbounded",
    no_anchor: "it named no read",
    invented_anchor: "its read never ran",
    unparseable_claimed_through: "its time bound was not a time",
  };
  if (claim.demoted) parts.push(why[claim.demoted] ?? claim.demoted.replace(/_/g, " "));
  return `${claim.statement} — ${parts.join(", ")}`;
}

// What a person is told of an investigation that stopped because its AWS account is no
// longer connected (#1093): the sentence its row recorded, which is the credential vend's
// or the platform's own, and never the reason's code. The second sentence is for a row
// that recorded none.
const ACCOUNT_NOT_CONNECTED =
  "This investigation's AWS account is no longer connected to Torque Assist, so the investigation stopped.";

// What a person is told of an investigation the workspace's model provider refused in a way
// no retry fixes (#1414): the runtime's sentence, which names the provider and its words, and
// never the reason's code. The second sentence is for a row that recorded none.
const PROVIDER_REFUSED =
  "This workspace's model provider refused the request, so the investigation stopped. Check Model provider.";

function failureLine(failure: Failure): string {
  if (failure.reason === "account_not_active") return failure.message ?? ACCOUNT_NOT_CONNECTED;
  if (failure.reason === "provider_refused") return failure.message ?? PROVIDER_REFUSED;
  return `${failure.reason ?? "Investigation failed"}${failure.message ? ` — ${failure.message}` : ""}`;
}

export function Summary({
  projection,
  status,
  onInspect,
}: {
  projection: RunProjection;
  status: string;
  onInspect?: (anchor: string) => void;
}) {
  const { conclusion, failure } = projection;
  // A refused run's gap and fallback summary embed the runtime's last refusal word for word; the
  // Summary says it once, in the Confidence field, and shows none of that text (#1434).
  const refused = conclusion ? refusedConclusion(projection, conclusion) : null;
  const runtimesText = (text: string | null) => refused !== null && refused.embedsRefusal(text);
  const explanation: Record<string, string> = {
    waiting:
      "Waiting for the investigation's first events. The conclusion will appear here when it is reached.",
    running:
      "The investigation is gathering evidence. No conclusion yet.",
    paused:
      "The investigation is paused. Inspect the request beside the evidence for the next step.",
    complete:
      "The investigation completed. No conclusion is available in the received events yet.",
    failed:
      "The investigation stopped with a failure. Inspect Timeline for the details.",
    budget_exhausted:
      "The investigation stopped because its budget was exhausted. The evidence collected so far remains available.",
  };
  return (
    <section className="investigation-conclusion">
      <p className="eyebrow">{conclusion ? "Root cause" : runStatusLabel(status)}</p>
      {conclusion ? (
        <>
          {refusalNotice(conclusion) ? <Notice>{refusalNotice(conclusion)}</Notice> : null}
          <CorrectionNotice projection={projection} />
          <h2>{refused?.heading ?? conclusion.root_cause ?? "Root cause not determined"}</h2>
          {conclusion.summary && !runtimesText(conclusion.summary) ? <p>{conclusion.summary}</p> : null}
          <dl className="investigation-facts">
            <div>
              <dt>Confidence</dt>
              <dd>
                <ConfidenceBand projection={projection} conclusion={conclusion} onInspect={onInspect} />
              </dd>
            </div>
            {!conclusion.mechanism && runtimesText(conclusion.mechanism_gap) ? null : (
              <Field
                label={conclusion.mechanism ? "Mechanism" : "Mechanism gap"}
                value={conclusion.mechanism ?? conclusion.mechanism_gap ?? "Unknown"}
              />
            )}
            <Field
              label="Trigger"
              value={conclusion.trigger ?? "Unknown"}
            />
            <Field
              label="Severity"
              value={conclusion.severity ?? "Unknown"}
            />
          </dl>
          <TextList heading="Key facts" items={conclusion.key_facts} />
          <TextList
            heading="Recommendations"
            items={conclusion.recommendations}
            empty="No recommendations."
          />
          <TextList
            heading="Alternatives considered"
            items={(conclusion.alternatives_considered ?? []).map(claimLine)}
          />
        </>
      ) : (
        <>
          <h2>{runStatusLabel(status)}</h2>
          <p>
            {explanation[status] ??
              "This status is not recognised. Inspect Timeline for the available events."}
          </p>
        </>
      )}
      {failure ? <Notice tone="bad">{failureLine(failure)}</Notice> : null}
      {projection.clarification ? (
        <div>
          <h3>Clarification requested</h3>
          <p>{projection.clarification.question}</p>
        </div>
      ) : null}
    </section>
  );
}

/** A chat turn's retraction of a recorded finding (#391). The finding stays as
 *  it was recorded; this says it was later withdrawn, and what is true instead. */
function RetractionNotice({ retraction }: { retraction: Retraction }) {
  return (
    <Notice tone="bad">
      Retracted in chat: {retraction.claim}. Instead: {retraction.correction}
      {retraction.evidence ? ` (evidence: ${retraction.evidence})` : null}
    </Notice>
  );
}

/** The conclusion is never rewritten, so the Summary says when a chat turn has since
 *  retracted a finding beneath it, and links to each one (#1355). */
function CorrectionNotice({ projection }: { projection: RunProjection }) {
  const retracted = projection.findings
    .map((finding, index) => ({ finding, index }))
    .filter(({ finding }) => finding.retraction);
  if (retracted.length === 0) return null;
  return (
    <Notice tone="bad" standing>
      A later chat turn corrected part of this conclusion. Retracted:{" "}
      {retracted.map(({ finding, index }, position) => (
        <span key={finding.finding_id || index}>
          {position > 0 ? "; " : null}
          <a href={`#${supportingFindingId(index)}`}>
            {finding.summary || "Untitled finding"}
          </a>
        </span>
      ))}
      .
    </Notice>
  );
}

/** Where a supporting finding sits in the Summary's list, for the correction notice to link to. */
function supportingFindingId(index: number): string {
  return `supporting-finding-${index}`;
}

/** A retracted finding's mark where it is listed, readable without opening anything (#1355). */
function RetractedMark({ retraction }: { retraction: Retraction }) {
  return (
    <span className="investigation-retracted">
      <Chip tone="bad">Retracted</Chip> Instead: {retraction.correction}
    </span>
  );
}

/** The Evidence panel: exported for its render tests (`RunPage.test.ts`). */
export function Evidence({ projection }: { projection: RunProjection }) {
  const tools = projection.timeline.filter(
    (row) => row.tool_use_id || row.status.startsWith("tool_"),
  );
  return (
    <div className="stack investigation-evidence">
      <section>
        <h2>Findings</h2>
        {projection.findings.length === 0 ? (
          <p className="muted">No findings yet.</p>
        ) : (
          projection.findings.map((finding, index) => (
            <details
              className="investigation-detail"
              key={finding.finding_id || index}
              id={`finding-${index}`}
            >
              <summary>
                {finding.summary || "Untitled finding"}
                {finding.retraction ? (
                  <>
                    {" "}
                    <Chip tone="bad">Retracted</Chip>
                  </>
                ) : null}
              </summary>
              <div className="stack stack--tight">
                {finding.retraction ? (
                  <RetractionNotice retraction={finding.retraction} />
                ) : null}
                <p className="muted">
                  {[finding.severity, finding.status, finding.agent]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <TextList
                  items={finding.key_facts}
                  empty="No supporting facts."
                />
                <FindingSupport
                  evidence={findingEvidence(projection, finding)}
                />
              </div>
            </details>
          ))
        )}
      </section>
      <section>
        <h2>Hypotheses</h2>
        {projection.hypotheses.length === 0 ? (
          <p className="muted">No hypotheses yet.</p>
        ) : (
          projection.hypotheses.map((hypothesis, index) => (
            <details
              className="investigation-detail"
              key={hypothesis.hypothesis_id || index}
            >
              <summary>
                {hypothesis.statement || "Untitled hypothesis"}
              </summary>
              <div className="stack stack--tight">
                <p className="muted">
                  {hypothesis.untestable
                    ? "Unresolved — could not be tested"
                    : hypothesis.status}
                </p>
                <TextList
                  items={hypothesis.evidence}
                  empty="No evidence."
                />
                <Association
                  sources={projection.timeline.filter(
                    (row) =>
                      row.type === "hypothesis" &&
                      row.payload.hypothesis_id === hypothesis.hypothesis_id,
                  )}
                  tools={tools}
                />
              </div>
            </details>
          ))
        )}
      </section>
      <section>
        <h2>Tool records</h2>
        <p className="muted">
          A tool record is linked to a finding only when their identities
          match.
        </p>
        {tools.length === 0 ? (
          <p>No tool records available.</p>
        ) : (
          tools.map((row) => (
            <details
              className="investigation-detail"
              key={row.key}
              id={toolAnchor(row)}
            >
              <summary>
                {row.tool_name ?? label(row.status)} ·{" "}
                {label(toolStatus(row, projection.timeline))}
              </summary>
              <div className="stack stack--tight">
                {row.tool_input !== undefined ? (
                  <div>
                    <h3>Input</h3>
                    <Json value={row.tool_input} />
                  </div>
                ) : (
                  <p>Tool input unavailable.</p>
                )}
                {row.tool_output_preview ? (
                  <div>
                    <h3>Output preview</h3>
                    <pre className="investigation-json">
                      {row.tool_output_preview}
                    </pre>
                  </div>
                ) : (
                  <p>Tool output preview unavailable.</p>
                )}
                {row.tool_error ? (
                  <Notice tone="bad">{row.tool_error}</Notice>
                ) : null}
                <RecordedDetails row={row} />
              </div>
            </details>
          ))
        )}
      </section>
    </div>
  );
}

function FindingSupport({ evidence }: { evidence: FindingEvidence }) {
  // The read the finding's anchor names, and nothing nearer: an unanchored or
  // unmatched finding says so instead of borrowing a link (#483).
  if (evidence.kind === "unanchored") {
    return (
      <p className="muted">
        Supporting read unavailable: this finding has no anchor.
      </p>
    );
  }
  if (evidence.kind === "missing") {
    return (
      <p className="muted">
        Supporting read unavailable: read {evidence.anchor.tool_seq} is not in
        this investigation's record.
      </p>
    );
  }
  return (
    <ul className="facts">
      <li>
        <ToolLink row={evidence.row} />
      </li>
    </ul>
  );
}

function ToolLink({ row }: { row: TimelineRow }) {
  return (
    <a
      href={`#${toolAnchor(row)}`}
      onClick={() => {
        const detail = document.getElementById(
          toolAnchor(row),
        ) as HTMLDetailsElement | null;
        if (detail) {
          detail.open = true;
          detail.querySelector("summary")?.focus();
        }
      }}
    >
      Inspect {row.tool_name ?? "tool record"}
    </a>
  );
}

function Association({
  sources,
  tools,
}: {
  sources: TimelineRow[];
  tools: TimelineRow[];
}) {
  // Join an explicit tool call identity, never a guess based on prose or proximity.
  const ids = new Set(
    sources.flatMap((row) =>
      typeof row.payload.tool_use_id === "string"
        ? [row.payload.tool_use_id]
        : [],
    ),
  );
  const matches = tools.filter(
    (row) => row.tool_use_id && ids.has(row.tool_use_id),
  );
  return matches.length === 0 ? (
    <p className="muted">
      Supporting tool record unavailable: nothing links it to this finding.
    </p>
  ) : (
    <ul className="facts">
      {matches.map((row) => (
        <li key={row.key}>
          <ToolLink row={row} />
        </li>
      ))}
    </ul>
  );
}

function Timeline({ projection }: { projection: RunProjection }) {
  return (
    <div className="stack">
      <section>
        <h2>Timeline</h2>
        {projection.timeline.length === 0 ? (
          <p>No events yet.</p>
        ) : (
          <ol className="investigation-timeline">
            {[...projection.timeline]
              .sort((a, b) => a.seq - b.seq)
              .map((row) => (
                <li key={row.key}>
                  <span className="investigation-number">{row.seq}</span>
                  <details className="investigation-detail">
                    <summary>
                      {label(toolStatus(row, projection.timeline))}
                      {row.tool_name ? ` · ${row.tool_name}` : ""}
                    </summary>
                    <div className="stack stack--tight">
                      {row.message ? <p>{row.message}</p> : null}
                      <RecordedDetails row={row} />
                    </div>
                  </details>
                </li>
              ))}
          </ol>
        )}
      </section>
      <section className="investigation-reasoning">
        <h2>Reasoning · live only</h2>
        <p className="muted">
          Shown live only: it is not kept as evidence, and a reload does not
          bring it back.
        </p>
        {projection.ephemeral.reasoning ? (
          <pre className="investigation-json">
            {projection.ephemeral.reasoning}
          </pre>
        ) : (
          <p>No live reasoning received in this connection.</p>
        )}
      </section>
    </div>
  );
}

/** The phases after which the platform has written, or refused to write, the change (#262). */
const WRITTEN_PHASES: ReadonlySet<string> = new Set(["applied", "verified", "failed"]);

export function ProposalArea({ projection, children }: { projection: RunProjection; children?: ReactNode }) {
  const hasRecords =
    projection.requestedAction ||
    projection.actions.length ||
    projection.consents.length ||
    projection.outcome;
  return (
    <aside
      className="investigation-proposals stack"
      aria-label="Proposals and outcomes"
    >
      <h2>Proposals and outcomes</h2>
      {children}
      {!hasRecords ? (
        <p className="muted">
          Nothing has been proposed or changed. Recommendations alone do not
          authorise a change.
        </p>
      ) : null}
      {projection.requestedAction ? (
        <section className="stack stack--tight">
          <h3>Proposal</h3>
          <Payload value={projection.requestedAction} />
          <a className="btn btn--primary" href={filterPath(INBOX_KINDS.change_approval.filter)}>
            Review {INBOX_KINDS.change_approval.label.toLowerCase()} →
          </a>
        </section>
      ) : null}
      {projection.actions.map((action) => (
        <section className="stack stack--tight" key={action.change_id}>
          <h3>Change {label(action.phase)}</h3>
          <p className="muted">{action.change_id}</p>
          <Payload value={action.payload} />
          {action.phase === "expired" ? (
            <p>This request expired. Review {INBOX_KINDS.change_approval.label.toLowerCase()} for current requests.</p>
          ) : null}
          {WRITTEN_PHASES.has(action.phase) ? <ChangeAudit changeId={action.change_id} /> : null}
        </section>
      ))}
      {projection.consents.map((consent) => (
        <section className="stack stack--tight" key={consent.request_id}>
          <h3>Consent {label(consent.phase)}</h3>
          <Payload value={consent.payload} />
          {consent.phase === "requested" ? (
            <a href={filterPath(INBOX_KINDS.consent_request.filter)}>
              Review {INBOX_KINDS.consent_request.label.toLowerCase()} requests →
            </a>
          ) : null}
        </section>
      ))}
      {projection.outcome ? (
        <section className="stack stack--tight">
          <h3>Observed outcome</h3>
          <Payload value={projection.outcome} />
        </section>
      ) : null}
    </aside>
  );
}

function Payload({ value }: { value: Record<string, unknown> }) {
  return (
    <>
      {typeof value.message === "string" ? <p>{value.message}</p> : null}
      <details>
        <summary>Details</summary>
        <Json value={value} />
      </details>
    </>
  );
}
function RecordedDetails({ row }: { row: TimelineRow }) {
  return (
    <>
      <dl className="investigation-facts">
        <Field label="Sequence" value={String(row.seq)} />
        {row.tool_seq !== undefined ? (
          <Field label="Read" value={String(row.tool_seq)} />
        ) : null}
        <Field label="Emitter" value={row.emitter} />
        {row.attempt_token ? (
          <Field label="Attempt" value={row.attempt_token} />
        ) : null}
      </dl>
      <Json value={row.payload} />
    </>
  );
}
function Json({ value }: { value: unknown }) {
  return (
    <pre className="investigation-json">{JSON.stringify(value, null, 2)}</pre>
  );
}
/**
 * The run-level row an accepted call of each start-only tool is followed by (#1356,
 * #1374). Accepting one ends its segment, so the runtime records its start and never
 * its completion (`agent/hooks.py`: `ConcludeHook`, `RequestActionHook`,
 * `RequestConsentHook`): `conclude` ends the run with its `complete`, and the two
 * requests pause it on their own exit row.
 */
const ACCEPTED_BY: Readonly<Record<string, string>> = {
  conclude: "complete",
  request_action: "request_action",
  request_consent: "request_consent",
};

/**
 * A tool row's status as Evidence and Timeline read it. A start of a start-only tool
 * that its run-level row followed was accepted, unless a `tool_refused` row names the
 * same call. A chat turn's rows are never the run's.
 */
function toolStatus(row: TimelineRow, timeline: TimelineRow[]): string {
  const acceptedBy = row.tool_name === undefined ? undefined : ACCEPTED_BY[row.tool_name];
  if (row.status !== "tool_started" || acceptedBy === undefined) return row.status;
  const refused = timeline.some(
    (other) => other.type === "tool_refused" && row.tool_use_id && other.payload.tool_use_id === row.tool_use_id,
  );
  if (refused)
    return "tool_refused";
  const accepted = timeline.some(
    (other) => other.type === acceptedBy && other.seq > row.seq && !other.attempt_token?.startsWith("chat:"),
  );
  return accepted ? "tool_completed" : row.status;
}
/** The seq of the latest chat turn's terminal on the stream, or 0: what its debit commits with. */
function latestChatTurnTerminal(timeline: TimelineRow[]): number {
  let latest = 0;
  for (const row of timeline)
    if (isTerminal(row.type as RunStatus) && row.attempt_token?.startsWith("chat:") && row.seq > latest)
      latest = row.seq;
  return latest;
}
function toolAnchor(row: TimelineRow) {
  return `evidence-tool-${encodeURIComponent(row.key)}`;
}
/** The record's facts under the title: the account, the three clocks, the ticket and the Slack posts.
 *  Started and Finished are the stream's where it has them (#1374), so a live page and a reload
 *  show one value; the record's stand in until the stream's rows arrive. */
export function RunMeta({
  record,
  clocks,
}: {
  record: RunRecord;
  clocks?: { started_at: string | null; finished_at: string | null };
}) {
  const started = clocks?.started_at ?? record.started_at;
  const finished = clocks?.finished_at ?? record.finished_at;
  return (
    <dl className="investigation-meta">
      <Field label="Account" value={record.account_id ?? "Unknown"} />
      <Field label="Dispatched" value={time(record.dispatched_at)} />
      <Field label="Started" value={time(started)} />
      {finished ? <Field label="Finished" value={time(finished)} /> : null}
      <RunTickets tickets={record.tickets ?? []} />
      <RunSlackPosts posts={record.slack_posts ?? []} />
    </dl>
  );
}

/** What the run cost, as the ledger debited it, and its token counts (#1373). The record is
 *  read again at the terminal (#1356), so a run that finishes while open moves from "not known"
 *  to its figures with no reload. */
export function RunCostLines({ record }: { record: RunRecord }) {
  const cost = record.cost;
  if (!cost) return null;
  const totals = record.token_usage?.totals;
  let line: string;
  if (!isTerminal(record.status as RunStatus)) {
    line = "Cost: not known until the investigation finishes";
  } else {
    line = cost.credits === null ? "Cost: no credits charged" : `Cost: ${credits(cost.credits)}`;
    if (cost.chat_turns > 0)
      line += `, plus ${credits(cost.chat_turn_credits)} for ${cost.chat_turns} chat turn${cost.chat_turns === 1 ? "" : "s"}`;
  }
  return (
    <>
      <p className="muted">{line}</p>
      {totals ? (
        <p className="muted">
          {`Tokens: ${count(totals.total_input_tokens)} input · ${count(totals.total_output_tokens)} output · ${count(totals.cache_read_input_tokens)} cache-read`}
        </p>
      ) : null}
    </>
  );
}

function credits(value: number): string {
  return `${value.toLocaleString()} credit${value === 1 ? "" : "s"}`;
}

function count(value: number | undefined): string {
  return value === undefined ? "unknown" : value.toLocaleString();
}

/** The incident's ticket, linking to the issue in the workspace's queue (#265, the mock's screen 1). */
export function RunTickets({ tickets }: { tickets: RunTicket[] }) {
  if (tickets.length === 0) return null;
  return (
    <div>
      <dt>Ticket</dt>
      <dd>
        {tickets.map((ticket, index) => (
          <span key={`${ticket.kind}:${ticket.key}`}>
            {index > 0 ? ", " : null}
            <TicketLink ticket={ticket} />
            <span className="muted"> · {queueName(ticket.kind)}</span>
          </span>
        ))}
      </dd>
    </div>
  );
}

/** The messages the run's Slack deliveries posted, each linking to Slack (#818). */
export function RunSlackPosts({ posts }: { posts: SlackPost[] }) {
  if (posts.length === 0) return null;
  return (
    <div>
      <dt>Slack</dt>
      <dd>
        {posts.map((post, index) => (
          <span key={`${post.channel}:${post.ts}`}>
            {index > 0 ? ", " : null}
            <SlackPostLink post={post} />
          </span>
        ))}
      </dd>
    </div>
  );
}

/** The band, its recorded reasons, and the help that says whose estimate it is
 *  (#1141). Never the stored number; the cut-offs live in `./confidence`. */
function ConfidenceBand({
  projection,
  conclusion,
  onInspect,
}: {
  projection: RunProjection;
  conclusion: NonNullable<RunProjection["conclusion"]>;
  onInspect?: (anchor: string) => void;
}) {
  const band = confidenceBand(conclusion);
  const refused = refusedConclusion(projection, conclusion);
  if (refused) return <RefusedConfidence band={band} refused={refused} onInspect={onInspect} />;
  const reasons = confidenceReasons(projection, conclusion);
  return (
    <div className="confidence">
      <p className="confidence__band">{band}</p>
      {reasons.length > 0 ? (
        <ul className="confidence__reasons">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
      <details className="confidence__help">
        <summary>About this confidence</summary>
        <p className="muted">{CONFIDENCE_HELP}</p>
      </details>
    </div>
  );
}

/** A refused run's field: no band, no number, no disclosure -- why it refused, and the record's limits (#1434).
 *  The link opens the refusal's record in Evidence, where the runtime's own words are. */
function RefusedConfidence({
  band,
  refused,
  onInspect,
}: {
  band: string;
  refused: RefusedConclusion;
  onInspect?: (anchor: string) => void;
}) {
  const row = refused.row;
  return (
    <div className="confidence">
      <p className="confidence__band">{band}</p>
      <p>{refused.headline}</p>
      <p>
        {refused.explanation}
        {row ? (
          <>
            {" "}
            <a
              href={`#${toolAnchor(row)}`}
              onClick={(event) => {
                if (!onInspect) return;
                event.preventDefault();
                onInspect(toolAnchor(row));
              }}
            >
              Read the refusal in Evidence
            </a>
          </>
        ) : null}
      </p>
      {refused.unverified.length > 0 ? (
        <>
          <p>Not verified:</p>
          <ul className="confidence__reasons">
            {refused.unverified.map((statement) => (
              <li key={statement}>{statement}</li>
            ))}
          </ul>
        </>
      ) : null}
      {refused.limits.length > 0 ? (
        <>
          <p className="confidence__limits">Limits of the evidence:</p>
          <ul className="confidence__reasons">
            {refused.limits.map((limit) => (
              <li key={limit}>{limit}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function Field({ label: name, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{name}</dt>
      <dd>{value}</dd>
    </div>
  );
}
function TextList({
  heading,
  items,
  empty,
}: {
  heading?: string;
  items: string[];
  empty?: string;
}) {
  return items.length || empty ? (
    <section>
      {heading ? <h3>{heading}</h3> : null}
      {items.length ? (
        <ul className="facts">
          {items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="muted">{empty}</p>
      )}
    </section>
  ) : null;
}
function label(value: string) {
  const readable = value.replace(/_/g, " ");
  return readable.charAt(0).toUpperCase() + readable.slice(1);
}
